import Vue from 'vue';
import Vuex from 'vuex';
import bus from '@/utils/EventBus';
import { nanoid } from 'nanoid';
import userStore from './user';
import notification from './notification';
import events from './events';
import devices from './devices';
import awsInfo from '../../public/websocket';
import stats from './stats';
import lighthouse from './lighthouse';
import googleAuth from './googleAuth';

Vue.use(Vuex);

const store = new Vuex.Store({
  state: {
    loading: false,
    onvif: null,
    currentUrl: null,
    changeRoute: false,
    selectedDevice: null,
    currentCamera: null,
    currentUpdatedCamera: null,
    addCameraData: null,
  },
  mutations: {
    onvifSearch(state, payload) {
      state.onvif = payload;
      if (payload !== null) {
        state.loading = false;
      }
    },
    url(state, payload) {
      state.currentUrl = payload;
    },
    changeMyRoute(state, payload) {
      state.changeRoute = payload;
    },
    changeCurrentCamera(state, payload) {
      state.currentCamera = payload;
    },
    changeCurrentUpdatedCamera(state, payload) {
      state.currentUpdatedCamera = payload;
    },
    changeAddCameraData(state, payload) {
      state.addCameraData = payload;
    },
  },
  actions: {
    changeUrl(state, payload) {
      state.commit('url', payload);
      setTimeout(() => {
        state.commit('url', null);
      }, 100);
    },
    GetDeviceList() {
      awsInfo.list();
    },
    liveView(state, camera) {
      awsInfo.liveView(camera);
    },
    getOnvifDetail(state, {
      device, mac, username, password,
    }) {
      awsInfo.getOnvifDetail(device, mac, username, password);
    },
    listCameras(state, id) {
      awsInfo.listCameras(id);
    },
    updateCamera(state, { deviceId, cameraId, camera }) {
      awsInfo.updateCamera(deviceId, cameraId, camera);
      store.commit('changeCurrentCamera', camera);
    },
    setAddCamera(state, payload) {
      store.commit('changeAddCameraData', payload);
    },
    sendDeviceNameInformation(state, { deviceId }) {
      const device = store.getters.deviceId(deviceId);
      const deviceName = device.name;

      if (device.cameras.length <= 0) {
        return new Promise((resolve) => {
          resolve();
        });
      }

      const cameras = device.cameras.map((camera) => ({
        suid: camera.id,
        name: camera.name,
      }));

      const transactionId = nanoid();

      const liveviewMessage = {
        action: 'liveview',
        data: {
          camId: cameras[0].suid,
          transactionId,
          webRtcData: {
            config: 'setDeviceDetails',
            name: deviceName,
            cameras,
          },
        },
      };

      return new Promise((resolve) => {
        awsInfo.aws.send_object(liveviewMessage.action, liveviewMessage.data);
        resolve();
      });
    },

  },
  getters: {
    onvifCameraFinder: (state) => (ip) => state.onvif.cameras.find((camera) => camera.IP === ip),
  },
  modules: {
    user: userStore,
    notification,
    devices,
    events,
    stats,
    googleAuth,
    lighthouse,
  },

});

export default store;

function sendCameraDetails(cameraId, cameraName, deviceName) {
  const transactionId = nanoid();

  const liveviewMessage = {
    action: 'liveview',
    data: {
      camId: cameraId,
      transactionId,
      webRtcData: {
        config: 'setDeviceDetails',
        cameras: [
          {
            suid: cameraId,
            name: cameraName,
          },
        ],
      },
    },
  };

  if (deviceName !== null && deviceName !== undefined && deviceName !== '') {
    liveviewMessage.data.webRtcData.name = deviceName;
  }

  awsInfo.aws.send_object(liveviewMessage.action, liveviewMessage.data);
}

const messageHandlers = {

  GetMyInfo(message) {
    store.commit('setUserInfo', {
      name: message.data.user.name,
      organisation: message.data.org.name,
      userType: message.data.user.type,
    });
  },

  GetDeviceList(message) {
    store.commit('addDevices', message.data.devices);
  },

  DeviceOnvifDiscover(message) {
    if (message.status === 200) {
      store.commit('onvifSearch', message.data);
    }
  },

  GetOnvifConfiguration(message) {
    if (message.status === 200) {
      const url = message.data.onvifResponse[0].encoder_uri;
      store.dispatch('changeUrl', url);
    }
    if (message.status === 500) {
      // store.dispatch('addNotification', {
      // type: 'red',
      // title: 'Failed to find camera',
      // comment: '',
      // });
      store.dispatch('changeUrl', false);
    }
  },

  AddCamera(message) {
    if (message.status === 200) {
      awsInfo.list();
      store.dispatch('addNotification', {
        type: 'green',
        title: 'Camera successfully added!',
        comment: '',
      });
      store.commit('changeMyRoute', true);
      setTimeout(() => {
        store.commit('changeMyRoute', false);
      }, 100);

      if (message.data.result === 'added') {
        const { cameraId } = message.data;
        const { deviceId, name: cameraName } = store.state.addCameraData;

        const device = store.getters.deviceId(deviceId);
        const isAddedCamera = device.cameras.some((camera) => camera.id === cameraId)
          && device.cameras.length === 1;
        const deviceName = (device.cameras.length === 0 || isAddedCamera) ? device.name : '';

        sendCameraDetails(cameraId, cameraName, deviceName);
      }
    }
    if (message.status === 500) {
      store.dispatch('addNotification', {
        type: 'red',
        title: 'Incorrect details!',
        comment: message.data.error.message,
      });
    }
  },

  DeleteCamera(message) {
    if (message.status === 200) {
      store.dispatch('addNotification', {
        type: 'green',
        title: 'Camera removed!',
        comment: '',
      });
      store.dispatch('GetDeviceList');
      store.commit('changeMyRoute', true);
      setTimeout(() => {
        store.commit('changeMyRoute', false);
      }, 100);
    }
  },
  GetCamInfo(message) {
    if (message.status === 200) {
      store.commit('changeCurrentUpdatedCamera', message.data.camera);
    }
  },

  UpdateCamera(message) {
    if (message.status === 200) {
      awsInfo.getCameraInfo(store.state.currentCamera.id);
      awsInfo.list();
      if (message.data.result === 'updated') {
        const { cameraId } = message.data;
        const cameraName = store.state.currentCamera.name;

        // const device = store.getters.deviceFind(message.data.cameraId);
        // const deviceName = device.name;

        sendCameraDetails(cameraId, cameraName, '');
      }
    }
  },
  AddDevice(message) {
    if (message.status === 200) {
      this.$store.dispatch('addNotification', {
        type: 'green',
        title: 'Device Added!',
        comment: '',
      });
    }
  },
  liveview(message) {
    console.debug('liveview:', message);
  },
};
bus.$on('receivedMessageEvent', (message) => {
  if (typeof messageHandlers[message.response] === 'function') {
    messageHandlers[message.response](message);
  } else {
    console.warn('No handler for message type', message.response);
  }
});
